/**
 * Word tokeniser for the spell-check plugin.
 *
 * Splits text into checkable words with their character offsets. A "word" is a
 * run of unicode letters with optional *internal* apostrophes/hyphens
 * (e.g. "don't", "well-being"). Anything containing a digit (claim numbers,
 * codes like "WC-2026", "abc123") is skipped — those aren't dictionary words.
 * Leading/trailing apostrophes and hyphens are trimmed off the reported word.
 */

export type WordToken = {
  /** The word to spell-check (leading/trailing '/- trimmed). */
  word: string;
  /** Inclusive-exclusive character offsets into the source text. */
  start: number;
  end: number;
};

// Runs starting with a letter or number, then letters/numbers/'/- .
const RUN = /[\p{L}\p{N}][\p{L}\p{N}'-]*/gu;
// A pure word: letters with only *internal* apostrophes/hyphens.
const PURE_WORD = /^\p{L}+(?:['-]\p{L}+)*$/u;
const TRIM = /^['-]+|['-]+$/gu;

export function tokenizeWords(text: string): WordToken[] {
  const out: WordToken[] = [];
  for (const m of text.matchAll(RUN)) {
    const raw = m[0];
    const runStart = m.index ?? 0;
    // Trim leading/trailing apostrophes/hyphens and adjust offsets.
    const leading = raw.length - raw.replace(/^['-]+/u, "").length;
    const word = raw.replace(TRIM, "");
    if (!word || !PURE_WORD.test(word)) continue; // has a digit, or empty → skip
    const start = runStart + leading;
    out.push({ word, start, end: start + word.length });
  }
  return out;
}
